import React from 'react';
import InsightArticle from '../../components/InsightArticle';
import { useLanguage } from '../../context/LanguageContext';

const BeautyOutcomes = () => {
  const { lang } = useLanguage();

  const content = {
    en: {
      title: "Beauty Clinics: Stop Selling Treatments, Start Selling Outcomes",
      description: "Why procedure-led marketing creates friction and how switching to outcome-based messaging drives more consultations.",
      category: "Beauty Growth",
      date: "Jan 24, 2026",
      keyTakeaway: "Nobody wakes up wanting a treatment; they wake up wanting a result. Your marketing should reflect that reality.",
      body: (
        <>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Most clinic content reads like a procedure menu: HIFU, chemical peel, dermal filler, laser resurfacing. Correct information, wrong starting point. Almost nobody searching or scrolling starts with "I want a chemical peel." They start with <span className="text-orange-500 font-semibold">"I want my skin to look less tired"</span> or <span className="text-orange-500 font-semibold">"I want my jawline to look more defined."</span>
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            The treatment is the means. The outcome is the decision.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">Why this gap costs bookings</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            When content and messaging lead with the procedure name, it puts the <span className="text-orange-500 font-semibold">burden of translation</span> on the client — they have to already know that a chemical peel solves their specific concern. Most don't. They'll keep scrolling until they find a clinic that speaks in the language they actually think in: how will I look, how will I feel, what will people notice.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Clinics that lead with outcomes remove that translation step entirely. "Smoother, more even skin tone without downtime" gets a very different response than "Chemical Peel — €120," even when it's describing the exact same service.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">What this looks like in practice</h3>
          <div className="space-y-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Content</h4>
              <p className="text-gray-400">Instead of "New treatment: Profhilo," try "Tired-looking skin, without the tired-looking result — here's what actually helps." Name the treatment once, further down, for the people who want the specifics. Lead with the concern and the result.</p>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Pricing pages</h4>
              <p className="text-gray-400">Group by concern (fine lines, skin texture, volume loss, pigmentation), not alphabetically by procedure name. A client browsing by concern finds their answer in seconds.</p>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Consultations</h4>
              <p className="text-gray-400">Start with "what's bothering you about it" before explaining what the clinic offers. This makes the recommendation land because it's framed as solving their specific thing, not pitching a menu item.</p>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Before/afters</h4>
              <p className="text-gray-400">Caption them with the concern solved. "6 weeks post-treatment — texture and tone" works harder than "Post-HIFU result."</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">The trust side of this</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Outcome-first framing isn't just a conversion tactic — it also signals something clients actually care about: that the clinic <span className="text-orange-500 font-semibold">understands the problem</span>, not just the procedure. A client who feels understood books faster and negotiates less on price.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">The shift, in one sentence</h3>
          <p className="mb-10 text-xl md:text-2xl text-white font-black italic leading-tight border-l-4 border-orange-500 pl-6 py-2">
            "Nobody wakes up wanting a treatment. They wake up wanting a result."
          </p>

          <div className="mt-12 p-8 bg-zinc-900 border border-orange-500/30 rounded-[2rem]">
            <p className="text-xl text-white font-bold mb-4 leading-tight">
              This is exactly what a Content Day is built to produce.
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Photos and short-form video shot around the outcome and the questions prospects actually ask. Feed that into an <span className="text-white font-bold">AI Lead Assistant</span> that qualifies and books the resulting DMs, and outcome-led messaging turns straight into appointments.
            </p>
            <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
              Test the flow: send "SYSTEM" to my Instagram.
            </p>
          </div>
        </>
      )
    },
    es: {
      title: "Clínicas de Estética: Deja de Vender Tratamientos, Empieza a Vender Resultados",
      description: "Por qué el marketing centrado en procedimientos genera fricción y cómo cambiar a mensajes basados en resultados impulsa más consultas.",
      category: "Crecimiento Estético",
      date: "24 Ene 2026",
      keyTakeaway: "Nadie se despierta queriendo un tratamiento; se despiertan queriendo un resultado. Tu marketing debe reflejar esa realidad.",
      body: (
        <>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            La mayoría del contenido de las clínicas parece un menú de procedimientos: HIFU, peeling químico, rellenos dérmicos, rejuvenecimiento con láser. Información correcta, punto de partida equivocado. Casi nadie empieza buscando <span className="text-orange-500 font-semibold">"Quiero un peeling químico"</span>. Empiezan con <span className="text-orange-500 font-semibold">"Quiero que mi piel se vea menos cansada"</span>.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            El tratamiento es el medio. El resultado es la decisión.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">Por qué esta brecha cuesta reservas</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Cuando el contenido se centra en el nombre del procedimiento, pones la <span className="text-orange-500 font-semibold">carga de la traducción</span> en el cliente: ellos tienen que saber que un peeling soluciona su problema. La mayoría no lo sabe. Seguirán buscando hasta encontrar una clínica que hable su idioma: cómo me veré, cómo me sentiré.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">Cómo aplicarlo en la práctica</h3>
          <div className="space-y-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Contenido</h4>
              <p className="text-gray-400">En lugar de "Nuevo tratamiento: Profhilo", prueba con "Piel con aspecto cansado, sin el resultado de cansancio: esto es lo que ayuda". Nombra el tratamiento después, para los que quieren detalles técnicos.</p>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Tarifas</h4>
              <p className="text-gray-400">Agrupa por preocupación (líneas de expresión, textura, pérdida de volumen), no alfabéticamente por procedimiento. Un cliente encuentra su respuesta en segundos.</p>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-2">Consultas</h4>
              <p className="text-gray-400">Empieza con "¿qué es lo que más te preocupa?" antes de explicar lo que ofreces. Esto hace que la recomendación encaje perfectamente con su necesidad.</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4 uppercase tracking-tighter">El cambio, en una frase</h3>
          <p className="mb-10 text-xl md:text-2xl text-white font-black italic leading-tight border-l-4 border-orange-500 pl-6 py-2">
            "Nadie se despierta queriendo un tratamiento. Se despiertan queriendo un resultado."
          </p>

          <div className="mt-12 p-8 bg-zinc-900 border border-orange-500/30 rounded-[2rem]">
            <p className="text-xl text-white font-bold mb-4 leading-tight">
              Esto es exactamente para lo que está diseñado un Día de Contenido.
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Fotos y vídeos cortos centrados en el resultado y las dudas reales. Conéctalo con un <span className="text-white font-bold">Asistente de Leads con IA</span> que cualifique y reserve esos DMs, y los resultados se convertirán en citas.
            </p>
            <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
              Prueba el sistema: envía "SISTEMA" a mi Instagram.
            </p>
          </div>
        </>
      )
    }
  };

  const t = content[lang] || content.en;

  return (
    <InsightArticle
      title={t.title}
      description={t.description}
      category={t.category}
      date={t.date}
      readTime="5"
      keyTakeaway={t.keyTakeaway}
    >
      {t.body}
    </InsightArticle>
  );
};

export default BeautyOutcomes;